import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon } from '@heroicons/react/24/outline';
import {
  checkServiceAtCoords,
  clearLeadContext,
  LeadContext,
  newPlacesSessionToken,
  placeAutocomplete,
  placeDetails,
  PlacePrediction,
  plansPathForCity,
  saveLeadContext,
} from '../utils/leadContext';
import CoverageCheckAnimation, { withMinCheckDuration } from './CoverageCheckAnimation';

type Status = 'idle' | 'checking' | 'available' | 'unavailable' | 'error';

type VerifiedPlace = {
  placeId: string;
  formatted: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  lat: number;
  lng: number;
};

type HomeAddressCheckProps = {
  stayOnPage?: boolean;
  onAvailable?: (ctx: LeadContext) => void;
  variant?: 'default' | 'dark';
  /** When false, dark variant stays hidden until parent reveals it */
  revealed?: boolean;
};

const HomeAddressCheck: React.FC<HomeAddressCheckProps> = ({
  stayOnPage,
  onAvailable,
  variant = 'default',
  revealed = true,
}) => {
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [open, setOpen] = useState(false);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [verified, setVerified] = useState<VerifiedPlace | null>(null);
  const [highlight, setHighlight] = useState(-1);

  const sessionRef = useRef(newPlacesSessionToken());
  const debounceRef = useRef<number | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const skipSuggestRef = useRef(false);
  const checkSeqRef = useRef(0);
  const suggestSeqRef = useRef(0);
  const navTimerRef = useRef<number | null>(null);

  const bumpCheck = () => {
    checkSeqRef.current += 1;
    if (navTimerRef.current != null) {
      window.clearTimeout(navTimerRef.current);
      navTimerRef.current = null;
    }
    return checkSeqRef.current;
  };

  useEffect(() => {
    clearLeadContext();
    setAddress('');
    setVerified(null);
    setStatus('idle');
    setMessage('');
    return () => {
      bumpCheck();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (skipSuggestRef.current) {
      skipSuggestRef.current = false;
      return;
    }
    const q = address.trim();
    if (q.length < 3 || verified) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const seq = ++suggestSeqRef.current;
    debounceRef.current = window.setTimeout(async () => {
      setLoadingSuggest(true);
      try {
        const preds = await placeAutocomplete(q, sessionRef.current);
        if (seq !== suggestSeqRef.current) return;
        setSuggestions(preds);
        setOpen(preds.length > 0);
        setHighlight(-1);
      } catch (_) {
        if (seq !== suggestSeqRef.current) return;
        setSuggestions([]);
        setOpen(false);
      } finally {
        if (seq === suggestSeqRef.current) setLoadingSuggest(false);
      }
    }, 220);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [address, verified]);

  const resetStatus = () => {
    if (status !== 'idle' && status !== 'checking') {
      setStatus('idle');
      setMessage('');
    }
  };

  const pickSuggestion = async (pred: PlacePrediction) => {
    const seq = bumpCheck();
    skipSuggestRef.current = true;
    clearLeadContext();
    setVerified(null);
    setAddress(pred.description);
    setSuggestions([]);
    setOpen(false);
    setStatus('checking');
    setMessage('Verifying address with Google…');

    try {
      const details = await placeDetails(pred.placeId, sessionRef.current);
      if (seq !== checkSeqRef.current) return;
      sessionRef.current = newPlacesSessionToken();
      if (!details.lat || !details.lng) throw new Error('Could not verify that address');

      const place: VerifiedPlace = {
        placeId: pred.placeId,
        formatted: details.formatted,
        address: details.address,
        city: details.city || 'Kingman',
        state: details.state || 'AZ',
        zipCode: details.zipCode || '',
        lat: details.lat,
        lng: details.lng,
      };
      setVerified(place);
      setAddress(details.formatted || pred.description);
      // Stay in checking — do not flip to idle (that race lets a second pick start)
      await runCoverageCheck(place, seq);
    } catch (err: any) {
      if (seq !== checkSeqRef.current) return;
      setVerified(null);
      setStatus('error');
      setMessage(err?.message || 'Could not verify that address. Pick another suggestion.');
    }
  };

  const runCoverageCheck = async (place: VerifiedPlace, existingSeq?: number) => {
    const seq = existingSeq ?? bumpCheck();
    setStatus('checking');
    setMessage('');

    try {
      const result = await withMinCheckDuration(
        checkServiceAtCoords(place.formatted || place.address, place.lat, place.lng, {
          fullLos: false,
        }),
        2600
      );
      if (seq !== checkSeqRef.current) return;

      const available = !!(result.available || result.canConnect) && !result.losBlocked;
      const city = place.city || 'Kingman';
      const ctx = {
        address: place.address || place.formatted,
        city,
        state: place.state || 'AZ',
        zipCode: place.zipCode || '',
        lat: place.lat,
        lng: place.lng,
        formatted: place.formatted,
        available,
        tower: result.tower,
        technology: result.technology,
        checkedAt: new Date().toISOString(),
      };
      saveLeadContext(ctx);

      if (available) {
        setStatus('available');
        setMessage(`Great news — service is available at ${place.formatted}! Choose your plan below.`);
        if (stayOnPage) {
          onAvailable?.(ctx);
          navTimerRef.current = window.setTimeout(() => {
            if (seq !== checkSeqRef.current) return;
            document.getElementById('home-plans')?.scrollIntoView({ behavior: 'smooth' });
          }, 800);
        } else {
          navTimerRef.current = window.setTimeout(() => {
            if (seq !== checkSeqRef.current) return;
            navigate(plansPathForCity(city), {
              state: { leadContext: ctx, fromHomeCheck: true },
            });
            window.scrollTo(0, 0);
          }, 1800);
        }
      } else {
        clearLeadContext();
        setStatus('unavailable');
        setMessage(
          result.losBlocked
            ? 'No service available — line of sight to our towers is blocked by terrain.'
            : 'No service available at this address.'
        );
      }
    } catch (err: any) {
      if (seq !== checkSeqRef.current) return;
      clearLeadContext();
      setStatus('error');
      setMessage(err?.message || 'Something went wrong. Please try again or call 928-565-6707.');
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'checking') return;

    if (verified) {
      await runCoverageCheck(verified);
      return;
    }

    const q = address.trim();
    if (q.length < 3) {
      setStatus('error');
      setMessage('Start typing your street address, then pick it from the Google suggestions.');
      return;
    }

    setStatus('checking');
    setMessage('Looking up address…');
    try {
      const preds = await placeAutocomplete(q, sessionRef.current);
      if (!preds.length) {
        setStatus('error');
        setMessage('No matching Google addresses found. Try a fuller street address.');
        return;
      }
      setSuggestions(preds);
      setOpen(true);
      setStatus('error');
      setMessage('Select your address from the Google suggestions below to continue.');
    } catch (err: any) {
      setStatus('error');
      setMessage(err?.message || 'Address suggestions unavailable. Please try again.');
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      void pickSuggestion(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const isDark = variant === 'dark';

  if (isDark) {
    return (
      <div
        className={`w-full max-w-xl text-left transition-all duration-150 ease-out ${
          revealed
            ? 'max-h-[900px] opacity-100 translate-y-0 pt-1 pb-1 overflow-visible'
            : 'max-h-0 opacity-0 -translate-y-1 pt-0 pb-0 overflow-hidden pointer-events-none'
        }`}
        aria-hidden={!revealed}
      >
        <form onSubmit={onSubmit} className="p-0">
          <div className="relative" ref={wrapRef}>
            <div
              className="absolute -top-[7px] left-10 h-3.5 w-3.5 rotate-45 border-l border-t border-white/30 bg-[#0c1220]"
              aria-hidden="true"
            />
            <div className="relative rounded-2xl border border-white/30 bg-[#0c1220]/92 backdrop-blur-md px-5 py-5 sm:px-6 sm:py-6">
              <p className="text-base sm:text-lg font-semibold text-white leading-snug">
                Enter your address to check Availability
              </p>

              <div className="mt-4 flex items-center gap-3 rounded-xl border border-white/25 bg-black/30 px-4 py-3 focus-within:border-blue-400/70 transition-colors">
                <MapPinIcon className="h-5 w-5 text-white/70 flex-shrink-0" aria-hidden="true" />
                <input
                  id="home-address-input"
                  type="text"
                  value={address}
                  onChange={(e) => {
                    bumpCheck();
                    setAddress(e.target.value);
                    setVerified(null);
                    clearLeadContext();
                    resetStatus();
                  }}
                  onFocus={() => {
                    if (suggestions.length) setOpen(true);
                  }}
                  onKeyDown={onKeyDown}
                  placeholder="Enter your service address"
                  className="flex-1 min-w-0 bg-transparent border-0 p-0 text-white text-base placeholder:text-white/45 focus:outline-none focus:ring-0"
                  autoComplete="off"
                  disabled={status === 'checking'}
                  tabIndex={revealed ? 0 : -1}
                  role="combobox"
                  aria-expanded={open}
                  aria-autocomplete="list"
                  aria-controls="home-address-listbox"
                />
                <button
                  type="submit"
                  disabled={status === 'checking' || !revealed}
                  className="flex-shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-60"
                  aria-label={status === 'checking' ? 'Checking availability' : 'Check availability'}
                >
                  <ArrowRightIcon className="h-5 w-5" />
                </button>
              </div>

              {open && revealed && (suggestions.length > 0 || loadingSuggest) && (
                <ul
                  id="home-address-listbox"
                  className="absolute z-50 left-5 right-5 sm:left-6 sm:right-6 mt-2 max-h-64 overflow-auto rounded-xl border border-white/15 bg-[#111827] shadow-xl"
                  role="listbox"
                >
                  {loadingSuggest && !suggestions.length && (
                    <li className="px-4 py-3 text-sm text-white/50">Searching Google addresses…</li>
                  )}
                  {suggestions.map((pred, idx) => (
                    <li key={pred.placeId}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={idx === highlight}
                        className={`w-full text-left px-4 py-3 text-sm border-b border-white/10 last:border-0 ${
                          idx === highlight ? 'bg-blue-600/20' : 'hover:bg-white/5'
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => void pickSuggestion(pred)}
                      >
                        <div className="font-semibold text-white">{pred.mainText}</div>
                        {pred.secondaryText && (
                          <div className="text-white/50 text-xs mt-0.5">{pred.secondaryText}</div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {verified && status !== 'checking' && (
            <p className="mt-2 px-1 text-xs font-medium text-green-300">
              ✓ Google verified: {verified.formatted}
            </p>
          )}

          {status === 'checking' && (
            <CoverageCheckAnimation active label="Checking your address…" />
          )}

          {message && status !== 'checking' && (
            <div
              className={`mt-3 rounded-xl px-4 py-3 text-sm font-medium ${
                status === 'available'
                  ? 'bg-green-500/15 text-green-200 border border-green-400/30'
                  : status === 'unavailable'
                    ? 'bg-orange-500/15 text-orange-200 border border-orange-400/30'
                    : status === 'error'
                      ? 'bg-red-500/15 text-red-200 border border-red-400/30'
                      : 'bg-blue-500/15 text-blue-200 border border-blue-400/30'
              }`}
            >
              {message}
            </div>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="w-full text-left max-w-2xl mx-auto mt-8">
      <form onSubmit={onSubmit} className="p-0">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1" ref={wrapRef}>
            <input
              id="home-address-input"
              type="text"
              value={address}
              onChange={(e) => {
                bumpCheck();
                setAddress(e.target.value);
                setVerified(null);
                clearLeadContext();
                resetStatus();
              }}
              onFocus={() => {
                if (suggestions.length) setOpen(true);
              }}
              onKeyDown={onKeyDown}
              placeholder="Start typing — pick your address from Google…"
              className="w-full rounded-xl border border-white/40 bg-white/95 px-4 py-3 text-gray-900 text-base shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
              autoComplete="off"
              disabled={status === 'checking'}
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
              aria-controls="home-address-listbox"
            />
            {open && (suggestions.length > 0 || loadingSuggest) && (
              <ul
                id="home-address-listbox"
                className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-auto rounded-xl border border-gray-200 bg-white shadow-xl"
                role="listbox"
              >
                {loadingSuggest && !suggestions.length && (
                  <li className="px-4 py-3 text-sm text-gray-500">Searching Google addresses…</li>
                )}
                {suggestions.map((pred, idx) => (
                  <li key={pred.placeId}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={idx === highlight}
                      className={`w-full text-left px-4 py-3 text-sm border-b border-gray-100 last:border-0 ${
                        idx === highlight ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => void pickSuggestion(pred)}
                    >
                      <div className="font-semibold text-gray-900">{pred.mainText}</div>
                      {pred.secondaryText && (
                        <div className="text-gray-500 text-xs mt-0.5">{pred.secondaryText}</div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="submit"
            disabled={status === 'checking'}
            className="rounded-xl bg-white hover:bg-gray-100 text-airmax-blue font-bold px-6 py-3 transition-colors disabled:opacity-60 whitespace-nowrap shadow-lg"
          >
            {status === 'checking' ? 'Checking…' : 'Check Availability'}
          </button>
        </div>

        {verified && status !== 'checking' && (
          <p className="mt-2 px-1 text-xs font-medium text-green-700">
            ✓ Google verified: {verified.formatted}
          </p>
        )}

        {status === 'checking' && (
          <CoverageCheckAnimation active label="Checking your address…" />
        )}

        {message && status !== 'checking' && (
          <div
            className={`mt-3 rounded-xl px-4 py-3 text-sm font-medium ${
              status === 'available'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : status === 'unavailable'
                  ? 'bg-orange-50 text-orange-900 border border-orange-200'
                  : status === 'error'
                    ? 'bg-red-50 text-red-800 border border-red-200'
                    : 'bg-blue-50 text-blue-900 border border-blue-200'
            }`}
          >
            {message}
          </div>
        )}
      </form>
      <p className="text-center text-blue-100 text-xs mt-3">
        Pick from Google suggestions · Takes about 10 seconds · Or call 928-565-6707
      </p>
    </div>
  );
};

export default HomeAddressCheck;
